import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import {
  ShoppingCart,
  Timer,
  MessageCircle,
  Pause,
  CreditCard,
  ToggleLeft,
  Sliders,
  Sparkles,
  BookOpen,
  ArrowUpRight,
  CheckCircle2,
  Lock,
} from "lucide-react";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardFooter from "@/components/DashboardFooter";
import { useOptionalMerchant } from "@/lib/useMerchant";
import BackToDashboard from "@/components/BackToDashboard";

/**
 * Plain-English guide to the abandoned cart reminder automation — what
 * counts as "abandoned," the two-message sequence and its default timing,
 * real example wording, and the specific conditions that stop a reminder
 * from sending. Companion to TabsGuide.jsx and StatusGuide.jsx, same
 * card-based layout. Growth & Pro only — see ProfileInfo.jsx for where
 * merchants configure this.
 */

function Bubble({ label, tag, text, tagColor }) {
  return (
    <div className="rounded-2xl border border-ink/10 bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-bold uppercase tracking-[0.12em] text-ink/50">{label}</p>
        <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${tagColor}`}>
          {tag}
        </span>
      </div>
      <div className="mt-3 rounded-2xl rounded-tl-sm bg-ink px-4 py-3 text-cream">
        <p className="text-[13px] leading-relaxed">{text}</p>
      </div>
    </div>
  );
}

function StopCard({ icon: Icon, title, desc }) {
  return (
    <div className="rounded-2xl border border-ink/10 bg-white p-4">
      <div className="flex items-center gap-2.5">
        <span className="grid h-8 w-8 flex-none place-items-center rounded-xl bg-hotpink/10 text-hotpink">
          <Icon size={14} strokeWidth={2.4} />
        </span>
        <p className="text-sm font-bold text-ink">{title}</p>
      </div>
      <p className="mt-2 text-[13px] leading-relaxed text-ink/65">{desc}</p>
    </div>
  );
}

export default function AbandonedCartGuide() {
  const { me } = useOptionalMerchant();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "Abandoned Cart Reminders Guide",
    description: "How Checkout DM automatically follows up with customers who add to cart but go quiet before checkout.",
    publisher: { "@type": "Organization", name: "Checkout DM", url: "https://checkoutdm.com" },
    mainEntityOfPage: "https://checkoutdm.com/abandoned-cart-guide",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://checkoutdm.com/" },
      { "@type": "ListItem", position: 2, name: "Abandoned Cart Reminders Guide", item: "https://checkoutdm.com/abandoned-cart-guide" },
    ],
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Abandoned Cart Reminders Guide | Checkout DM"
        description="How Checkout DM automatically follows up with customers who add to cart but go quiet before checkout."
        path="/abandoned-cart-guide"
        structuredData={[articleSchema, breadcrumbSchema]}
      />
      <DashboardHeader me={me} />

      <main className="mx-auto max-w-4xl px-6 pt-10 md:px-10">
        {me && <BackToDashboard />}

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Help</p>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl">
          How abandoned cart reminders work
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink/70 md:text-base">
          A customer adds something to their cart in DM, then goes quiet before checking out.
          This automation follows up for you — automatically, on your timing, and only twice — so
          you don't have to manually track every half-finished chat.
        </p>
        <p className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
          <Link to="/tabs-guide" className="inline-flex items-center gap-1.5 font-semibold text-hotpink hover:underline">
            <BookOpen size={13} /> What each dashboard tab means →
          </Link>
          <Link to="/status-guide" className="inline-flex items-center gap-1.5 font-semibold text-hotpink hover:underline">
            <Sparkles size={13} /> What each status badge means →
          </Link>
        </p>

        {/* What counts as abandoned */}
        <section className="mt-8 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
          <div className="flex items-center gap-3.5">
            <span className="grid h-12 w-12 flex-none place-items-center rounded-2xl bg-lilac text-ink">
              <ShoppingCart size={20} strokeWidth={2.2} />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink/50">The trigger</p>
              <h2 className="mt-0.5 font-display text-2xl font-black tracking-tight md:text-3xl">
                What counts as an abandoned cart
              </h2>
            </div>
          </div>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ink/70">
            Any conversation where the bot has items sitting in cart, but the customer hasn't
            finished paying. It doesn't matter whether they vanished right after adding a product,
            or got all the way to typing CHECKOUT and then stalled on their address — if there's an
            unpaid cart and the customer's gone quiet, it qualifies.
          </p>
          <div className="mt-2 rounded-2xl bg-mint/25 px-4 py-3">
            <p className="text-[11px] font-bold uppercase tracking-wide text-ink/50">Example</p>
            <p className="mt-0.5 text-[12px] leading-relaxed text-ink/80">
              A customer messages "do you have this in size M?", the bot adds a Yellow T-Shirt to
              their cart, and then... nothing. No reply, no checkout. That's an abandoned cart.
            </p>
          </div>
        </section>

        {/* The sequence */}
        <section className="mt-6 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
          <div className="flex items-center gap-3.5">
            <span className="grid h-12 w-12 flex-none place-items-center rounded-2xl bg-peach text-ink">
              <Timer size={20} strokeWidth={2.2} />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink/50">The sequence</p>
              <h2 className="mt-0.5 font-display text-2xl font-black tracking-tight md:text-3xl">
                Two reminders, then it stops — for good
              </h2>
            </div>
          </div>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ink/70">
            Every abandoned cart gets a maximum of two DMs. Never more, and never on a loop — once
            the second one sends, that customer goes silent unless they come back and touch their
            cart again.
          </p>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <Bubble
              label="First reminder"
              tag="Default 60 min"
              tagColor="bg-lilac text-ink"
              text="Hey! You left Yellow T-Shirt in your cart 🛒 Just reply CHECKOUT and I'll help you finish up right away!"
            />
            <Bubble
              label="Second reminder"
              tag="Default 20h"
              tagColor="bg-hotpink text-white"
              text="⏰ Don't miss out! Yellow T-Shirt in your cart is going fast — reply CHECKOUT now to complete your order!"
            />
          </div>
          <p className="mt-4 text-[12px] leading-relaxed text-ink/45">
            Both messages rotate between a few different wordings so repeat customers don't see the
            exact same DM twice, and both always tell the customer exactly what to type — CHECKOUT
            — so there's no confusion about what happens next. You choose the exact timing for both
            in your{" "}
            <Link to="/profile" className="font-semibold text-hotpink hover:underline">
              profile settings
            </Link>
            .
          </p>
        </section>

        {/* When it won't send */}
        <section className="mt-6 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
          <div className="flex items-center gap-3.5">
            <span className="grid h-12 w-12 flex-none place-items-center rounded-2xl bg-ink text-cream">
              <Pause size={20} strokeWidth={2.2} />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink/50">The guardrails</p>
              <h2 className="mt-0.5 font-display text-2xl font-black tracking-tight md:text-3xl">
                When a reminder won't send
              </h2>
            </div>
          </div>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ink/70">
            This is deliberately cautious — it only nudges customers who are genuinely still
            deciding, never ones who've already bought or who you're already talking to yourself.
          </p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <StopCard
              icon={CreditCard}
              title="They already paid"
              desc="The moment a cart turns into a paid order, reminders stop for it — there's nothing left to nudge them about."
            />
            <StopCard
              icon={Pause}
              title="You've paused the bot"
              desc="If you've manually paused replies for that customer to talk to them yourself, the automation stays out of the way too."
            />
            <StopCard
              icon={ToggleLeft}
              title="The feature is off"
              desc="Turned off entirely in your profile settings — no reminders send to anyone, on any cart, until you switch it back on."
            />
            <StopCard
              icon={Timer}
              title="Too much time has passed"
              desc="Instagram only allows DMs within 24 hours of a customer's last message. If that window's closed, we don't send — better silence than a failed, confusing message."
            />
          </div>
        </section>

        {/* Configuring */}
        <section className="mt-6 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
          <div className="flex items-center gap-3.5">
            <span className="grid h-12 w-12 flex-none place-items-center rounded-2xl bg-mint text-ink">
              <Sliders size={20} strokeWidth={2.2} />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink/50">Your control</p>
              <h2 className="mt-0.5 font-display text-2xl font-black tracking-tight md:text-3xl">
                Turning it on and setting your own timing
              </h2>
            </div>
          </div>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ink/70">
            Head to your{" "}
            <Link to="/profile" className="font-semibold text-hotpink hover:underline">
              profile page
            </Link>{" "}
            to switch reminders on or off entirely, and to set exactly how many minutes after a
            cart goes quiet each of the two messages should go out. There's no reason to match our
            defaults — some stores do better with a faster first nudge, others prefer to give
            customers more breathing room.
          </p>
          <div className="mt-4 flex items-start gap-2.5 rounded-2xl bg-ink/[0.03] p-4">
            <CheckCircle2 size={16} className="mt-0.5 flex-none text-ink/40" />
            <p className="text-[13px] leading-relaxed text-ink/65">
              Changes apply going forward only — a reminder that's already scheduled or already
              sent for an existing cart won't be affected by a timing change you make afterward.
            </p>
          </div>
        </section>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-[2rem] border border-hotpink/20 bg-hotpink/5 p-6 md:p-8">
          <div className="flex items-start gap-3">
            <span className="grid h-11 w-11 flex-none place-items-center rounded-2xl bg-hotpink/10 text-hotpink">
              <Lock size={18} />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Growth & Pro only</p>
              <p className="mt-1 max-w-md text-sm text-ink/70">
                Don't have this yet? It's one of the easiest ways to recover sales that almost
                happened — no extra work on your end once it's set up.
              </p>
            </div>
          </div>
          <Link
            to="/plans"
            className="cta-glow inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2.5 text-sm font-semibold text-white"
          >
            See plans <ArrowUpRight size={14} />
          </Link>
        </div>
      </main>

      <div className="mt-10">
        <DashboardFooter />
      </div>
    </div>
  );
}
